import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ChevronRightIcon,
  CubeIcon,
  FolderIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { projectService } from '../services/projectService';
import { formatDate, formatFileSize } from '../lib/format';
import { Download, Project, Release } from '../types';
import { EmptyState, LoadingState, PageHeader } from './ui';
import CreateProjectModal from './CreateProjectModal';

interface ReleaseWithProject extends Release {
  projectName: string;
}

interface DownloadWithContext extends Download {
  projectName: string;
  releaseVersion: string;
}

const Dashboard: React.FC = () => {
  const { t, language } = useLanguage();
  const { user, appUser } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [ownedCount, setOwnedCount] = useState(0);
  const [releases, setReleases] = useState<ReleaseWithProject[]>([]);
  const [releaseCount, setReleaseCount] = useState(0);
  const [downloads, setDownloads] = useState<DownloadWithContext[]>([]);
  const [downloadCount, setDownloadCount] = useState(0);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [owned, shared, favIds] = await Promise.all([
          projectService.getUserProjects(user.uid),
          projectService.getProjectsWhereUserIsMember(user.email || ''),
          projectService.getUserFavoriteProjectIds(user.uid),
        ]);
        const visibleOwned = owned.filter((p) => p.status !== 'deleted');
        const byId = new Map<string, Project>();
        [...visibleOwned, ...shared].forEach((p) => byId.set(p.id, p));
        const all = Array.from(byId.values());

        const releaseLists = await Promise.all(
          all.map((p) => projectService.getProjectReleases(p.id).catch(() => [] as Release[]))
        );
        const allReleases = releaseLists
          .flat()
          .map((r) => ({ ...r, projectName: byId.get(r.projectId)?.name || '' }))
          .sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime());
        const versionById = new Map(allReleases.map((r) => [r.id, r.version]));

        const downloadLists = await Promise.all(
          visibleOwned.map((p) => projectService.getProjectDownloads(p.id).catch(() => [] as Download[]))
        );
        const allDownloads = downloadLists
          .flat()
          .map((d) => ({
            ...d,
            projectName: byId.get(d.projectId)?.name || '',
            releaseVersion: versionById.get(d.releaseId) || '',
          }))
          .sort((a, b) => b.downloadedAt.getTime() - a.downloadedAt.getTime());

        if (cancelled) return;
        setProjects(all.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()));
        setOwnedCount(visibleOwned.length);
        setReleases(allReleases.slice(0, 6));
        setReleaseCount(allReleases.length);
        setDownloads(allDownloads.slice(0, 6));
        setDownloadCount(allDownloads.length);
        setFavoriteIds(favIds);
      } catch (error) {
        console.error('Error loading dashboard:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (loading) return <LoadingState />;

  const firstName = (appUser?.displayName || user?.displayName || '').split(' ')[0];
  const favorites = projects.filter((p) => favoriteIds.has(p.id));
  const recentProjects = projects.filter((p) => !favoriteIds.has(p.id)).slice(0, 5);

  const stats = [
    { label: t('dashboard.stats.projects'), value: projects.length, note: t('dashboard.stats.projectsNote', { owned: ownedCount }) },
    { label: t('dashboard.stats.releases'), value: releaseCount, note: t('dashboard.stats.releasesNote') },
    { label: t('dashboard.stats.downloads'), value: downloadCount, note: t('dashboard.stats.downloadsNote') },
  ];

  return (
    <div className="space-y-8">
      <PageHeader
        title={firstName ? t('dashboard.greeting', { name: firstName }) : t('dashboard.title')}
        description={t('dashboard.subtitle')}
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <PlusIcon className="h-4 w-4" />
            {t('projects.new')}
          </button>
        }
      />

      <dl className="card grid grid-cols-1 divide-y divide-gray-100 dark:divide-gray-800 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        {stats.map((stat) => (
          <div key={stat.label} className="px-5 py-4">
            <dt className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</dt>
            <dd className="mt-1 flex items-baseline gap-2">
              <span className="text-3xl font-bold tabular-nums tracking-tight text-gray-900 dark:text-white">{stat.value}</span>
              <span className="text-xs text-gray-400 dark:text-gray-500">{stat.note}</span>
            </dd>
          </div>
        ))}
      </dl>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <section className="card lg:col-span-3" aria-labelledby="recent-releases">
          <div className="flex items-center justify-between px-5 pb-2 pt-4">
            <h2 id="recent-releases" className="font-semibold text-gray-900 dark:text-white">
              {t('dashboard.recentReleases')}
            </h2>
          </div>
          {releases.length === 0 ? (
            <EmptyState icon={CubeIcon} title={t('dashboard.noReleases')} text={t('dashboard.noReleasesText')} compact />
          ) : (
            <ul className="px-2 pb-2">
              {releases.map((release) => (
                <li key={release.id}>
                  <Link
                    to={`/projects/${release.projectId}`}
                    className="group flex items-center gap-3 rounded-lg px-3 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700 dark:bg-primary-950 dark:text-primary-300">
                      <CubeIcon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate font-medium text-gray-900 dark:text-gray-100">{release.title}</p>
                        <span className="version-tag shrink-0">v{release.version}</span>
                      </div>
                      <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                        {release.projectName} — {formatFileSize(release.fileSize)}
                      </p>
                    </div>
                    <span className="hidden shrink-0 text-xs text-gray-400 sm:block">
                      {formatDate(release.uploadedAt, language)}
                    </span>
                    <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-300 group-hover:text-gray-500 dark:text-gray-600" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <div className="space-y-6 lg:col-span-2">
          {favorites.length > 0 && (
            <ProjectList
              title={t('dashboard.favorites')}
              icon={<StarSolid className="h-4 w-4 text-amber-400" />}
              projects={favorites.slice(0, 6)}
            />
          )}
          <ProjectList
            title={t('dashboard.recentProjects')}
            projects={recentProjects}
            action={
              <Link to="/projects" className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400">
                {t('dashboard.viewAll')}
              </Link>
            }
            empty={
              favorites.length === 0 ? (
                <EmptyState
                  icon={FolderIcon}
                  title={t('dashboard.noProjects')}
                  text={t('dashboard.noProjectsText')}
                  compact
                />
              ) : undefined
            }
          />
        </div>
      </div>

      {downloads.length > 0 && (
        <section className="card" aria-labelledby="recent-downloads">
          <h2 id="recent-downloads" className="px-5 pb-2 pt-4 font-semibold text-gray-900 dark:text-white">
            {t('dashboard.recentDownloads')}
          </h2>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('downloads.who')}</th>
                  <th>{t('downloads.what')}</th>
                  <th className="text-right">{t('downloads.when')}</th>
                </tr>
              </thead>
              <tbody>
                {downloads.map((d) => (
                  <tr
                    key={d.id}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    onClick={() => navigate(`/projects/${d.projectId}`)}
                  >
                    <td>
                      <div className="flex items-center gap-2">
                        <ArrowDownTrayIcon className="h-4 w-4 shrink-0 text-signal-600" />
                        <span className="font-medium text-gray-900 dark:text-gray-100">{d.downloadedByName}</span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap">
                      {d.projectName} {d.releaseVersion && <span className="version-tag ml-1">v{d.releaseVersion}</span>}
                    </td>
                    <td className="whitespace-nowrap text-right text-gray-500">{formatDate(d.downloadedAt, language, true)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onSuccess={(id) => {
            setShowCreate(false);
            navigate(`/projects/${id}`);
          }}
        />
      )}
    </div>
  );
};

const ProjectList: React.FC<{
  title: string;
  icon?: React.ReactNode;
  projects: Project[];
  action?: React.ReactNode;
  empty?: React.ReactNode;
}> = ({ title, icon, projects, action, empty }) => {
  const { language } = useLanguage();
  if (projects.length === 0 && !empty) return null;
  return (
    <section className="card">
      <div className="flex items-center justify-between px-5 pb-2 pt-4">
        <h2 className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white">
          {icon}
          {title}
        </h2>
        {action}
      </div>
      {projects.length === 0 ? (
        empty
      ) : (
        <ul className="px-2 pb-2">
          {projects.map((project) => (
            <li key={project.id}>
              <Link
                to={`/projects/${project.id}`}
                className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/60"
              >
                <span className="truncate text-sm font-medium text-gray-800 dark:text-gray-200">{project.name}</span>
                <span className="shrink-0 text-xs text-gray-400">{formatDate(project.updatedAt, language)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default Dashboard;
